import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiErrorResponseDTO } from '@app/shared/services/dtos/api-error-response.dto';
import { RequestError } from '@app/shared/types/enums/request-error.enum';
import { ApiError } from '@app/shared/types/models/api-error.model';
import { TranslateService } from '@ngx-translate/core';

/**
 * ApiErrorService - Parses API error payloads and maps error codes to i18n keys.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used by {@link httpErrorInterceptor} for global toasts and by
 * {@link LocalApiErrorHandler} / in-screen banners for local error display.
 */
@Injectable({ providedIn: 'root' })
export class ApiErrorService {
  private readonly translateService: TranslateService = inject(TranslateService);

  public parse(error: unknown): ApiError | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = this.asErrorBody(error.error);
    if (!body) {
      return null;
    }

    return {
      errorCode: body.errorCode ?? 'DefaultError',
      statusCode: body.statusCode ?? error.status,
      detail: body.detail ?? null,
      fieldErrors: body.errors ?? {}
    };
  }

  public parseOrDefault(error: unknown): ApiError {
    return (
      this.parse(error) ?? {
        errorCode: 'DefaultError',
        statusCode: error instanceof HttpErrorResponse ? error.status : 0,
        detail: null,
        fieldErrors: {}
      }
    );
  }

  public getTranslationKey(apiError: ApiError): string {
    const mappedKey = this.resolveMappedErrorKey(apiError);
    if (mappedKey) {
      return `request-errors.${mappedKey}`;
    }

    return 'request-errors.default-error';
  }

  public getMessage(error: unknown): string | null {
    const apiError = this.parse(error);
    if (!apiError) {
      return null;
    }

    const mappedKey = this.resolveMappedErrorKey(apiError);
    if (mappedKey) {
      return this.translateService.instant(`request-errors.${mappedKey}`);
    }

    const nameError = Array.isArray(apiError.fieldErrors['name']) ? apiError.fieldErrors['name'][0] : null;
    return nameError || apiError.detail || null;
  }

  private resolveMappedErrorKey(apiError: ApiError): string | undefined {
    if (this.hasLockedTaskFieldError(apiError.fieldErrors)) {
      return RequestError.TaskLocked;
    }

    return RequestError[apiError.errorCode as keyof typeof RequestError];
  }

  private hasLockedTaskFieldError(fieldErrors: Readonly<Record<string, readonly string[]>>): boolean {
    return Object.keys(fieldErrors).some(fieldKey => {
      const normalizedKey = fieldKey.toLowerCase();
      return normalizedKey === 'islocked' || normalizedKey.endsWith('.islocked');
    });
  }

  private asErrorBody(raw: unknown): ApiErrorResponseDTO | null {
    if (!raw || typeof raw !== 'object' || Object.keys(raw).length === 0) {
      return null;
    }

    const record = raw as Record<string, unknown>;
    const errors = this.readProperty<Readonly<Record<string, string[]>>>(record, 'errors');

    return {
      detail: this.readProperty<string>(record, 'detail'),
      errorCode: this.readProperty<string>(record, 'errorCode'),
      errors: errors && typeof errors === 'object' ? errors : undefined,
      statusCode: this.readProperty<number>(record, 'statusCode')
    };
  }

  private readProperty<T>(record: Record<string, unknown>, camelKey: string): T | undefined {
    const pascalKey = `${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
    const value = record[camelKey] ?? record[pascalKey];
    return value as T | undefined;
  }
}
